<template>
  <article>
    <AppBanner
      title="Sales Report"
      title-alignment="text-left flex-row max-w-[1600px]"
    />

    <div class="max-w-[1600px] mx-auto container-padding flex flex-col gap-6 my-8">
      <!-- Filters -->
      <div
        class="rounded-lg p-5 flex flex-col md:flex-row md:items-end gap-4 border-1 border-gray-300"
      >
        <!-- Date range -->
        <DatePicker
          v-model="dateRange"
          label="Date Range"
          range
        />

        <!-- Events -->
        <EventMultiselect v-model="selectedEventIds" />

        <!-- Run report -->
        <AppButton
          class="btn btn-solid btn-secondary h-10 lg:h-11 text-sm lg:text-base w-fit whitespace-nowrap"
          @click="loadReport"
        >
          Run report
        </AppButton>
      </div>

      <!-- Summary pills -->
      <div class="flex flex-wrap items-center gap-2">
        <div
          v-for="pill in summaryPills"
          :key="pill.label"
          class="flex items-center gap-1.5 text-sm text-gray-dark font-admin border border-gray rounded-full px-3 py-1"
        >
          <FontAwesomeIcon
            :icon="pill.icon"
            class="text-secondary text-xs"
          />
          {{ pill.label }}
        </div>
      </div>

      <!-- Stat cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          v-for="(card, index) in statCards"
          :key="card.label"
          class="rounded-lg p-6 border-1 border-gray-300"
          :class="index === 0 ? 'bg-gradient-to-b from-secondary-subtle to-white' : 'bg-white'"
        >
          <div class="flex items-center gap-2 mb-4">
            <div
              class="w-7 h-7 rounded flex items-center justify-center shrink-0"
              :class="index === 0 ? 'bg-white border border-gray-light' : 'bg-secondary-subtle'"
            >
              <FontAwesomeIcon
                :icon="card.icon"
                class="text-secondary text-xs"
              />
            </div>

            <span class="text-xs font-semibold text-gray-dark uppercase tracking-wide font-admin">{{ card.label }}</span>
          </div>

          <p
            class="font-admin font-bold text-primary mb-0 leading-none"
            style="font-size: 2rem;"
          >
            <span
              v-if="card.prefix"
              class="text-xl"
            >{{ card.prefix }}</span>
            {{ card.value }}
          </p>

          <p class="text-sm text-gray-dark font-admin mb-0 mt-2">
            {{ card.subtitle }}
          </p>
        </div>
      </div>

      <!-- Sales by event table -->
      <div
        class="rounded-lg border-1 border-gray-300"
      >
        <div class="flex items-start justify-between p-5">
          <div>
            <h2 class="font-bold text-primary font-admin text-base mb-0.5">
              Sales by event
            </h2>

            <p class="text-sm text-gray-dark font-admin mb-0">
              {{ rows.length }} events in the selected period.
            </p>
          </div>

          <AppButton
            class="btn btn-solid btn-secondary text-sm whitespace-nowrap"
            :disabled="rows.length === 0"
            @click="exportToCsv"
          >
            Export to CSV
          </AppButton>
        </div>

        <AppTable
          class="sales-report"
          :if-looped-items-exist="true"
        >
          <template #headings>
            <th>
              Event
            </th>
            <th>Date</th>
            <th class="text-right">
              Orders
            </th>
            <th class="text-right">
              Items
            </th>
            <th class="text-right">
              Revenue
            </th>
          </template>

          <template #rows>
            <tr
              v-for="row in rows"
              :key="row.eventSlug"
            >
              <td class="font-bold">
                {{ row.eventName }}
              </td>
              <td>{{ formatDate(row.date) }}</td>
              <td class="text-right">
                {{ Number(row.totalOrders).toLocaleString() }}
              </td>
              <td class="text-right">
                {{ Number(row.totalOrderItems).toLocaleString() }}
              </td>
              <td class="text-right font-bold">
                {{ string.formatCurrency(Number(row.revenue)) }}
              </td>
            </tr>

            <!-- Totals row -->
            <tr>
              <td
                colspan="2"
                class="font-bold"
              >
                Total
              </td>
              <td class="text-right font-bold">
                {{ totals.orders.toLocaleString() }}
              </td>
              <td class="text-right font-bold">
                {{ totals.items.toLocaleString() }}
              </td>
              <td class="text-right font-bold">
                {{ string.formatCurrency(totals.revenue) }}
              </td>
            </tr>
          </template>
        </AppTable>
      </div>
    </div>
  </article>
</template>

<script setup>
import '@vuepic/vue-datepicker/dist/main.css';

import { computed, ref, watch } from 'vue';

import { AppBanner, AppButton, AppTable } from '@/components/app';
import { DatePicker, EventMultiselect } from '@/components/forms/inputs';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faCalendar,
  faCalendarRange,
  faImages,
  faMoneyBillWave,
  faReceipt,
  faTag,
} from '@fortawesome/pro-solid-svg-icons';

import { useDateFormatter, useDayjs, useString, useHttp } from '@/composables';

import { HttpStatusCode } from 'axios';

const string = useString();
const dateFormatter = useDateFormatter();
const dayjs = useDayjs();

const dateRange = ref(null);

const selectedEventIds = ref([]);

const rows = ref([]);

const totals = computed(() => ({
  orders: rows.value.reduce((s, r) => s + Number(r.totalOrders), 0),
  items: rows.value.reduce((s, r) => s + Number(r.totalOrderItems), 0),
  revenue: rows.value.reduce((s, r) => s + Number(r.revenue), 0),
}));

const avgOrderValue = computed(() =>
  totals.value.orders > 0 ? Math.round(totals.value.revenue / totals.value.orders) : 0,
);

const formatDate = iso =>
  new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });

const lastRunFilters = ref(null);

const summaryPills = computed(() => {
  if (!lastRunFilters.value) return [];

  const { dateRange: dr, selectedEventIds: ids } = lastRunFilters.value;
  const pills = [];

  if (dr?.[0] && dr?.[1]) {
    const from = dateFormatter.formatShortDate(dr[0]);
    const to = dateFormatter.formatShortDate(dr[1]);
    pills.push({ icon: faCalendarRange, label: `${from} – ${to}` });
  }

  const eventLabel = ids.length === 0 ? 'All events' : `${ids.length} events selected`;
  pills.push({ icon: faCalendar, label: `${eventLabel} · ${rows.value.length} in range` });

  return pills;
});

const statCards = computed(() => [
  {
    label: 'Total Sales',
    icon: faMoneyBillWave,
    prefix: 'R',
    value: string.formatCurrency(totals.value.revenue, false, null),
    subtitle: 'incl. VAT',
    featured: true,
  },
  {
    label: 'Orders',
    icon: faReceipt,
    prefix: null,
    value: string.formatNumbers(totals.value.orders),
    subtitle: `${rows.value.length} events`,
    featured: false,
  },
  {
    label: 'Items Purchased',
    icon: faImages,
    prefix: null,
    value: string.formatNumbers(totals.value.items),
    subtitle: 'photos & prints',
    featured: false,
  },
  {
    label: 'Avg Order Value',
    icon: faTag,
    prefix: 'R',
    value: string.formatCurrency(avgOrderValue.value, false, null),
    subtitle: 'per order',
    featured: false,
  },
]);

const escapeCsvValue = (value) => {
  const str = String(value ?? '');
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const exportToCsv = () => {
  const headers = ['Event', 'Date', 'Orders', 'Items', 'Revenue (R)'];

  const dataRows = rows.value.map(row => [
    escapeCsvValue(row.eventName),
    escapeCsvValue(formatDate(row.date)),
    row.totalOrders,
    row.totalOrderItems,
    Number(row.revenue).toFixed(2),
  ].join(','));

  const totalsRow = [
    'Total', '', totals.value.orders, totals.value.items, totals.value.revenue.toFixed(2),
  ].join(',');

  const csv = [headers.join(','), ...dataRows, totalsRow].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `sales-report-${dayjs().format('YYYY-MM-DD')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const loadReport = async () => {
  const params = {};

  if (dateRange.value?.[0]) params.dateFrom = dayjs(dateRange.value[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
  if (dateRange.value?.[1]) params.dateTo = dayjs(dateRange.value[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
  if (selectedEventIds.value.length > 0) params.events = selectedEventIds.value;

  const response = await useHttp().get('/reports/sales', params);

  if (response.statusCode === HttpStatusCode.Ok) {
    rows.value = response.data;
    lastRunFilters.value = {
      dateRange: dateRange.value ? [...dateRange.value] : null,
      selectedEventIds: [...selectedEventIds.value],
    };
  }
};

const unwatch = watch(selectedEventIds, (ids) => {
  if (ids.length > 0) {
    unwatch();
    loadReport();
  }
});

</script>
